"use client";

import { useState, useEffect } from "react";
import { Gamepad2, Trophy, TrendingUp, Award, Clock, Flame, Target } from "lucide-react";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";

interface GameCandidate {
  name: string;
  probability: number;
  volume: number;
  slug: string;
  image: string | null;
}

interface PreviousWinner {
  year: number;
  game: string;
  developer: string;
}

interface GamingData {
  year: number;
  eventName: string;
  eventDate: string;
  candidates: GameCandidate[];
  previousWinners: PreviousWinner[];
  stats: {
    totalCandidates: number;
    totalVolume: string;
    frontrunner: string | null;
    frontrunnerOdds: number;
  };
}

interface GamingContextSidebarProps {
  eventTitle: string;
  marketQuestions: string[];
}

export default function GamingContextSidebar({
  eventTitle,
  marketQuestions,
}: GamingContextSidebarProps) {
  const [gamingData, setGamingData] = useState<GamingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState<string>("");
  const visRef = useVisibilityRef();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch("/api/gaming/goty");
        if (res.ok) {
          const data = await res.json();
          setGamingData(data);
        }
      } catch (error) {
      }
      setLoading(false);
    };

    fetchData();

    // Refresh every 60 seconds for live odds
    const interval = setInterval(() => { if (visRef.current) fetchData(); }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Countdown to event
  useEffect(() => {
    if (!gamingData?.eventDate) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const eventTime = new Date(gamingData.eventDate).getTime();
      const diff = eventTime - now;

      if (diff <= 0) {
        setCountdown("Live Now!");
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

      if (days > 0) {
        setCountdown(`${days}d ${hours}h`);
      } else {
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setCountdown(`${hours}h ${minutes}m`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [gamingData?.eventDate]);

  // Get probability color and style
  const getProbabilityStyle = (prob: number) => {
    if (prob >= 50) return { color: "text-[#CCFF00]", bg: "bg-[#CCFF00]", bgFaded: "bg-[#CCFF00]/20" };
    if (prob >= 10) return { color: "text-[#00FF9D]", bg: "bg-[#00FF9D]", bgFaded: "bg-[#00FF9D]/20" };
    if (prob >= 1) return { color: "text-yellow-400", bg: "bg-yellow-400", bgFaded: "bg-yellow-400/20" };
    return { color: "text-gray-500", bg: "bg-gray-500", bgFaded: "bg-gray-500/20" };
  };

  if (loading) {
    return (
      <div className="bg-[#18181D] rounded-[5px] border border-gray-800 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Gamepad2 className="w-5 h-5 text-gray-400 animate-pulse" />
          <span className="text-white font-satoshi font-bold">Loading odds...</span>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-12 bg-[#18181D]/50 rounded-[5px]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!gamingData) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Event Header with Frontrunner */}
      <div className="bg-[#18181D] border border-gray-800 rounded-[5px] overflow-hidden">
        <div className="border-b border-gray-800 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-gray-400" />
              <span className="text-white font-inter-tight font-bold text-sm uppercase tracking-wider">
                GOTY 2025
              </span>
            </div>
            <div className="flex items-center gap-1 bg-gray-800 px-2 py-1 rounded-[5px]">
              <Clock className="w-3 h-3 text-gray-400" />
              <span className="text-gray-400 text-xs font-bold">{countdown}</span>
            </div>
          </div>
        </div>

        {/* Frontrunner Highlight */}
        {gamingData.stats.frontrunner && (
          <div className="p-4 border-b border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <Flame className="w-4 h-4 text-[#FF6B35]" />
              <span className="text-gray-400 text-xs font-inter-tight uppercase">Frontrunner</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white font-satoshi font-bold text-lg truncate pr-2">
                {gamingData.stats.frontrunner}
              </span>
              <div className="bg-[#CCFF00] px-3 py-1 rounded-[5px]">
                <span className="text-black font-monument font-bold text-lg">
                  {gamingData.stats.frontrunnerOdds}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="p-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#1A1A1A]/50 rounded-[5px] p-3 text-center">
              <div className="text-xl font-monument font-bold text-white">
                {gamingData.stats.totalCandidates}
              </div>
              <div className="text-xs text-gray-400 font-satoshi">Games</div>
            </div>
            <div className="bg-[#1A1A1A]/50 rounded-[5px] p-3 text-center">
              <div className="text-xl font-monument font-bold text-white">
                {gamingData.stats.totalVolume}
              </div>
              <div className="text-xs text-gray-400 font-satoshi">Volume</div>
            </div>
          </div>
        </div>
      </div>

      {/* All Candidates with Odds */}
      <div className="bg-[#18181D] border border-gray-800 rounded-[5px] overflow-hidden">
        <div className="border-b border-gray-800 px-4 py-3">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-gray-400" />
            <h3 className="text-white font-inter-tight font-bold text-sm uppercase tracking-wider">
              Live Odds
            </h3>
          </div>
        </div>

        <div className="p-2 max-h-[350px] overflow-y-auto">
          {gamingData.candidates.slice(0, 15).map((game, idx) => {
            const style = getProbabilityStyle(game.probability);
            const isTopPick = idx === 0;

            return (
              <div
                key={game.slug}
                className={`flex items-center justify-between p-2.5 rounded-[5px] mb-1 transition-colors ${
                  isTopPick
                    ? "bg-gray-800/50 border border-gray-700"
                    : "bg-[#0f0f12]/30 hover:bg-[#0f0f12]/50"
                }`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className={`text-xs font-inter-tight font-bold w-4 shrink-0 ${
                    isTopPick ? "text-white" : "text-gray-500"
                  }`}>
                    {idx + 1}
                  </span>
                  {/* Game thumbnail */}
                  {game.image ? (
                    <img
                      src={game.image}
                      alt={game.name}
                      className="w-8 h-8 rounded-[5px] object-cover shrink-0 border border-gray-700"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-[5px] bg-[#1A1A1A]/50 shrink-0 flex items-center justify-center border border-gray-700">
                      <Gamepad2 className="w-4 h-4 text-gray-500" />
                    </div>
                  )}
                  <span className="text-white font-satoshi text-sm truncate">
                    {game.name}
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  {/* Probability bar */}
                  <div className="w-12 h-1.5 bg-[#1A1A1A] rounded-[5px] overflow-hidden">
                    <div
                      className={`h-full ${style.bg} rounded-[5px]`}
                      style={{ width: `${Math.max(game.probability, 2)}%` }}
                    />
                  </div>
                  <span className={`text-sm font-inter-tight font-bold min-w-[36px] text-right ${style.color}`}>
                    {game.probability}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {gamingData.candidates.length > 15 && (
          <div className="px-4 py-2 border-t border-gray-800 text-center">
            <span className="text-gray-500 text-xs font-inter-tight">
              +{gamingData.candidates.length - 15} more games
            </span>
          </div>
        )}
      </div>

      {/* Previous Winners */}
      <div className="bg-[#18181D] border border-gray-800 rounded-[5px] overflow-hidden">
        <div className="border-b border-gray-800 px-4 py-3">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-gray-400" />
            <h3 className="text-white font-inter-tight font-bold text-sm uppercase tracking-wider">
              Past Winners
            </h3>
          </div>
        </div>

        <div className="p-3 space-y-2">
          {gamingData.previousWinners.slice(0, 4).map((winner) => (
            <div
              key={winner.year}
              className="flex items-center justify-between bg-[#0f0f12]/30 rounded-[5px] p-2.5"
            >
              <div className="flex items-center gap-3">
                <span className="text-white font-monument font-bold text-sm">
                  {winner.year}
                </span>
                <div>
                  <span className="text-white font-satoshi text-xs font-bold">
                    {winner.game}
                  </span>
                  <span className="text-gray-500 text-xs block">
                    {winner.developer}
                  </span>
                </div>
              </div>
              <Trophy className="w-4 h-4 text-[#FFD700]" />
            </div>
          ))}
        </div>
      </div>

      {/* Data Source */}
      <div className="bg-[#18181D] border border-gray-800 rounded-[5px] p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-gray-400" />
            <span className="text-white font-satoshi font-bold text-xs">Live Market Odds</span>
          </div>
          <span className="text-gray-500 text-xs font-inter-tight">via Polymarket</span>
        </div>
      </div>
    </div>
  );
}
